/**
 * Unified Application Routes
 *
 * This component replaces the individual admin route files:
 * - PlatformAdmin.tsx
 * - MainAgencyAdmin.tsx
 * - MspAgencyAdmin.tsx
 * - AgencyAdmin.tsx
 * - CompanyAdmin.tsx
 *
 * Routes are rendered based on:
 * 1. User's role (determines layout variant)
 * 2. Backend permissions (from Redux store)
 * 3. Route configuration (from routeConfig.ts)
 *
 * @see routeConfig.ts for route definitions
 * @see PermissionWrapper.tsx for permission checking
 */

import React, {lazy, Suspense, useEffect, useState} from 'react'
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom'
import Layout from '../components/layout/Layout'
import S from '../components/common/styles/AdminStyles'
import {useGlobalSnackbar} from '../context/GlobalSnackbarContext'
import {TCognitoRole} from '../models/role'
import {userApi} from '../services/userApi'
import {useLazyGetUserByEmailQuery} from '../store/apis/user-api'
import {updateCurrentUser} from '../store/slices/root-slice'
import {useAppDispatch, useAppSelector} from '../store/store-hooks'
import EulaDialog from '../pages/sign-in/EulaDialog'
import {
  LayoutConfig,
  LayoutVariant,
  RouteConfig,
  PLATFORM_ADMIN_CONFIG,
  MAIN_AGENCY_ADMIN_CONFIG,
  MSP_AGENCY_ADMIN_CONFIG,
  AGENCY_ADMIN_CONFIG,
  COMPANY_ADMIN_CONFIG,
} from './routeConfig'
import LoadingBoundary from '@ui/loader'
import {ChunkErrorBoundary} from '@ui/error'

// ============================================================================
// LAZY LOADED COMPONENTS
// ============================================================================
// Note: Components with named exports use .then() to provide default export

// Dashboard Components
const Dashboard = lazy(() => import('../components/platform/dashboard'))
const MspAgencyDashboard = lazy(() => import('../components/msp/dashboard/Dashboard'))
const AgencyDashboard = lazy(() => import('../components/nonmsp/Dashboard/Dashboard'))
const CompanyDashboard = lazy(() => import('../components/company/dashboard'))

// Company Management
const ManageCompaniesPage = lazy(() =>
  import('../components/platform/company/ManageCompaniesPage').then((m) => ({
    default: m.ManageCompaniesPage,
  })),
)
const CompanyManagementPage = lazy(() =>
  import('../components/platform/company/CompanyManagementPage').then((m) => ({
    default: m.CompanyManagementPage,
  })),
)
const CompanyDetailPage = lazy(() =>
  import('../components/platform/company/CompanyDetailPage').then((m) => ({
    default: m.CompanyDetailPage,
  })),
)
const AddNewPositionPage = lazy(() =>
  import('../components/platform/company/AddNewPositionPage').then((m) => ({
    default: m.AddNewPositionPage,
  })),
)
const EditPositionPage = lazy(() =>
  import('../components/platform/company/EditPositionPage').then((m) => ({
    default: m.EditPositionPage,
  })),
)
const AddPositionTemplatePage = lazy(() =>
  import('../components/platform/company/AddPositionTemplatePage').then((m) => ({
    default: m.AddPositionTemplatePage,
  })),
)

// Agency Management
const ManageAgenciesPage = lazy(() =>
  import('../components/platform/agency/ManageAgenciesPage').then((m) => ({
    default: m.ManageAgenciesPage,
  })),
)
const AgencyManagementPage = lazy(() =>
  import('../components/platform/agency/AgencyManagementPage').then((m) => ({
    default: m.AgencyManagementPage,
  })),
)
const OnboardingAgency = lazy(() => import('../components/platform/onboarding/OnboardingAgency'))
const OnboardingCompany = lazy(() => import('../components/platform/onboarding/OnboardingCompany'))

// Global Routes
const CompensationCodesPage = lazy(() =>
  import('../components/platform/global/compensation-codes/CompensationCodesPage').then((m) => ({
    default: m.CompensationCodesPage,
  })),
)
const WCCatalogsPage = lazy(() =>
  import('../components/platform/global/wc-catalogs/WCCatalogsPage').then((m) => ({
    default: m.WCCatalogsPage,
  })),
)
const CatalogCompensationCodesPage = lazy(() =>
  import('../components/platform/global/wc-catalogs/CatalogCompensationCodesPage').then((m) => ({
    default: m.CatalogCompensationCodesPage,
  })),
)
const PaycodesPage = lazy(() =>
  import('../components/platform/global/paycodes/PaycodesPageClean').then((m) => ({
    default: m.PaycodesPage,
  })),
)
const PaycodeFormPage = lazy(() =>
  import('../components/platform/global/paycodes/PaycodeFormPage').then((m) => ({
    default: m.PaycodeFormPage,
  })),
)
const AuthoritiesPage = lazy(() =>
  import('../components/platform/global/authorities/AuthoritiesPage').then((m) => ({
    default: m.AuthoritiesPage,
  })),
)
const TaxManagementPage = lazy(() =>
  import('../components/platform/global/tax/TaxManagementPage').then((m) => ({
    default: m.TaxManagementPage,
  })),
)
const MetricsDashboardPage = lazy(() =>
  import('../components/platform/global/metrics').then((m) => ({
    default: m.MetricsDashboardPage,
  })),
)
const PlaceholderPage = lazy(() => import('../components/platform/global/PlaceholderPage'))
const ActivityLogsPage = lazy(() =>
  import('../components/activity-logs').then((m) => ({
    default: m.ActivityLogsPage,
  })),
)

// Payroll
const PayrollManagementPage = lazy(
  () => import('../components/platform/payroll/PayrollManagementPage'),
)
const PayrollReportsPage = lazy(() =>
  import('../components/platform/reports').then((m) => ({default: m.PayrollReportsPage})),
)

// Service Orders
const NewServiceOrderFormPage = lazy(
  () => import('../components/company/manage-orders/NewServiceOrderFormPage'),
)
const ServiceOrderDetailsPage = lazy(() =>
  import('../components/common/service-orders').then((m) => ({
    default: m.ServiceOrderDetailsPage,
  })),
)
const ServiceOrderSummary = lazy(
  () =>
    import(
      '../components/company/manage-orders/manage-order-tables/company-summary/ServiceOrderSummary'
    ),
)
const AgencyManageOrdersMain = lazy(
  () => import('../components/msp/manage-orders/agency-order-tables/AgencyManageOrdersMain'),
)
const AgencyPendingOrders = lazy(
  () => import('../components/msp/manage-orders/agency-order-tables/AgencyPendingOrders'),
)
const CompletedOrdersMain = lazy(
  () => import('../components/msp/manage-orders/completed-orders/CompletedOrdersMain'),
)
const ManageOrders = lazy(
  () => import('../components/company/manage-orders/manage-order-tables/ManageOrders'),
)
const OrderDetails = lazy(
  () => import('../components/company/manage-orders/manage-order-tables/OrderDetails'),
)
const CompanyCompletedOrdersMain = lazy(
  () => import('../components/company/manage-orders/completed-orders/CompanyCompletedOrdersMain'),
)
const CompanyCandidateViewMain = lazy(
  () =>
    import('../components/company/manage-orders/company-candidate-view/CompanyCandidateViewMain'),
)

// Job Orders
const MspAgencyJobOrderMain = lazy(
  () => import('../components/msp/manage-job-orders/job-orders-main/MspJobOrdersMain'),
)
const MspJobOrdersDetailsMain = lazy(
  () => import('../components/msp/manage-job-orders/job-order-details/MspJobOrdersDetailsMain'),
)
const JobOrdersDetailsSummaryMain = lazy(
  () => import('../components/common/job-orders/JobOrderDetailsSummaryMain'),
)
const JobOrderFromPrefill = lazy(
  () => import('../components/msp/manage-job-orders/job-order-summary/JobOrderFromPrefill'),
)

// Candidates & Prefill
const Candidates = lazy(() =>
  import('../components/common/candidates/Candidates').then((m) => ({default: m.Candidates})),
)
const PrefillMain = lazy(() => import('../components/common/prefill/PrefillMain'))
const AddOrEditPrefill = lazy(() => import('../components/common/prefill/AddOrEditPrefill'))

// Data Management
const ImportWizardMain = lazy(() =>
  import('../components/common/import-wizard/ImportWizardMain').then((m) => ({
    default: m.ImportWizardMain,
  })),
)
const DataMigrationPage = lazy(() =>
  import('../components/platform/data-migration/DataMigrationPage').then((m) => ({
    default: m.DataMigrationPage,
  })),
)
const MspDataCleanupMain = lazy(
  () => import('../components/msp/manage-data-cleanup/MspDataCleanupMain'),
)

// Users & Roles
const UsersAndRoles = lazy(() => import('../components/company/users-and-roles'))
const MspUsersAndRoles = lazy(() => import('../components/msp/users-and-roles'))
const GeneralUsers = lazy(() => import('../components/platform/users-and-roles/GeneralUsers'))
const GeneralAdmins = lazy(() => import('../components/platform/users-and-roles/GeneralAdmins'))
const AddOrEditGa = lazy(() => import('../components/platform/users-and-roles/AddOrEditGa'))

// Bill Codes
const AddBillCodePage = lazy(() =>
  import('../pages/AddBillCodePage').then((m) => ({default: m.AddBillCodePage})),
)
const CompanyAdminBillCodePage = lazy(() =>
  import('../pages/CompanyAdminBillCodePage').then((m) => ({default: m.CompanyAdminBillCodePage})),
)

// Candidate Distribution
const CandidateDistributionPageWrapper = lazy(() =>
  import('../components/ui/candidate-distribution-field/CandidateDistributionPageWrapper').then(
    (m) => ({default: m.CandidateDistributionPageWrapper}),
  ),
)
const CandidateDistributionPage = lazy(() =>
  import('../components/ui/candidate-distribution-field/CandidateDistributionPage').then((m) => ({
    default: m.CandidateDistributionPage,
  })),
)

// Company-specific
const ManagePositions = lazy(() => import('../components/company/positions/ManagePositions'))
const ManageTemplates = lazy(() => import('../components/company/manage-templates/ManageTemplates'))
const InvoicesPage = lazy(() =>
  import('../components/company/invoices/InvoicesPage').then((m) => ({default: m.InvoicesPage})),
)
const TimesheetPendingView = lazy(() =>
  import('../components/company/timesheets/TimesheetPendingView').then((m) => ({
    default: m.TimesheetPendingView,
  })),
)

// MSP-specific
const ManageClientPositions = lazy(
  () => import('../components/msp/manage-positions/ManageClientPositions'),
)
const MspManageTemplates = lazy(() => import('../components/msp/manage-templates/ManageTemplates'))
const ManagedSitesPage = lazy(() =>
  import('../components/platform/msp/ManagedSitesPage').then((m) => ({
    default: m.ManagedSitesPage,
  })),
)

// Other
const AccountDetails = lazy(() => import('../components/platform/onboarding/AccountDetails'))

// Page Wrapper
const PageWrapper = lazy(() =>
  import('../components/ui/layout/PageWrapper').then((m) => ({default: m.PageWrapper})),
)

// Roles & Permissions
const RolesPermissionsPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/RolesPermissionsPage').then((m) => ({
    default: m.RolesPermissionsPage,
  })),
)

// ============================================================================
// REDIRECT COMPONENTS
// These are simple components that handle navigation based on user context
// ============================================================================

// Agency Profile Redirect - redirects to the logged-in user's agency profile
const AgencyProfileRedirectComponent: React.FC = () => {
  const {loggedInAgencyId} = useAppSelector((state) => state.root)
  if (loggedInAgencyId) {
    return <Navigate to={`/manage-agencies/${loggedInAgencyId}/profile`} replace />
  }
  return <div>Error: No agency ID found</div>
}

// Company Profile Redirect - redirects to the logged-in user's company profile
const CompanyProfileRedirectComponent: React.FC = () => {
  const {loggedInCompanyId} = useAppSelector((state) => state.root)
  if (loggedInCompanyId) {
    return <Navigate to={`/manage-companies/${loggedInCompanyId}/profile`} replace />
  }
  return <div>Error: No company ID found</div>
}

// Add New User page - lazy loaded
const AddNewUserPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/AddNewUserPage').then((m) => ({
    default: m.AddNewUserPage,
  })),
)

// Edit User page - lazy loaded
const EditUserPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/EditUserPage').then((m) => ({
    default: m.EditUserPage,
  })),
)

// Role pages - lazy loaded
const AddRolePage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/AddRolePage').then((m) => ({
    default: m.AddRolePage,
  })),
)

const EditRolePage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/EditRolePage').then((m) => ({
    default: m.EditRolePage,
  })),
)

const RoleDetailPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/RoleDetailPage').then((m) => ({
    default: m.RoleDetailPage,
  })),
)

// RoleUsersPage removed - now handled by RoleDetailPage with ?view=users query param

const GlobalRolesPermissionsPage = lazy(() =>
  import('../components/Roles_And_Permissions/pages/GlobalRolesPermissionsPage').then((m) => ({
    default: m.GlobalRolesPermissionsPage,
  })),
)

// ============================================================================
// LOADING FALLBACK
// ============================================================================

const LoadingFallback = () => (
  <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
    <LoadingBoundary isLoading={true} />
  </div>
)

// ============================================================================
// COMPONENT MAP
// ============================================================================

/**
 * Maps component names from routeConfig to actual React components
 * Lazy-loaded components for code splitting
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LAZY_COMPONENT_MAP: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  // Dashboards
  Dashboard,
  MspAgencyDashboard,
  AgencyDashboard,
  CompanyDashboard,
  // Company Management
  ManageCompaniesPage,
  CompanyManagementPage,
  CompanyDetailPage,
  AddNewPositionPage,
  EditPositionPage,
  AddPositionTemplatePage,
  // Agency Management
  ManageAgenciesPage,
  AgencyManagementPage,
  OnboardingAgency,
  OnboardingCompany,
  // Global
  CompensationCodesPage,
  WCCatalogsPage,
  CatalogCompensationCodesPage,
  PaycodesPage,
  PaycodeFormPage,
  AuthoritiesPage,
  TaxManagementPage,
  MetricsDashboardPage,
  PlaceholderPage,
  ActivityLogsPage,
  // Payroll
  PayrollManagementPage,
  PayrollReportsPage,
  // Service Orders
  NewServiceOrderFormPage,
  ServiceOrderDetailsPage,
  ServiceOrderSummary,
  AgencyManageOrdersMain,
  AgencyPendingOrders,
  CompletedOrdersMain,
  ManageOrders,
  OrderDetails,
  CompanyCompletedOrdersMain,
  CompanyCandidateViewMain,
  // Job Orders
  MspAgencyJobOrderMain,
  MspJobOrdersDetailsMain,
  JobOrdersDetailsSummaryMain,
  JobOrderFromPrefill,
  // Candidates & Prefill
  Candidates,
  PrefillMain,
  AddOrEditPrefill,
  // Data
  ImportWizardMain,
  DataMigrationPage,
  MspDataCleanupMain,
  // Users & Roles
  UsersAndRoles,
  MspUsersAndRoles,
  GeneralUsers,
  GeneralAdmins,
  AddOrEditGa,
  // Bill Codes
  AddBillCodePage,
  CompanyAdminBillCodePage,
  // Candidate Distribution
  CandidateDistributionPageWrapper,
  CandidateDistributionPage,
  // Company
  ManagePositions,
  ManageTemplates,
  InvoicesPage,
  TimesheetPendingView,
  // MSP
  ManageClientPositions,
  MspManageTemplates,
  ManagedSitesPage,
  // Other
  AccountDetails,
  PageWrapper,
  // Roles & Permissions
  RolesPermissionsPage,
  UsersRolesPage: RolesPermissionsPage, // Alias to match routeConfig
  AddNewUserPage,
  EditUserPage,
  AddRolePage,
  EditRolePage,
  RoleDetailPage,
  GlobalRolesPermissionsPage,
}

/**
 * Maps component names to regular (non-lazy) React components
 * Used for redirect components and placeholders
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REGULAR_COMPONENT_MAP: Record<string, React.ComponentType<any>> = {
  AgencyProfileRedirect: AgencyProfileRedirectComponent,
  CompanyProfileRedirect: CompanyProfileRedirectComponent,
}

// ============================================================================
// MODULE PERMISSION TYPES
// ============================================================================

interface ModulePermission {
  key: string
  name: string
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  submodules?: ModulePermission[]
}

// ============================================================================
// PERMISSION HELPERS
// ============================================================================

/**
 * Module key aliases for role-specific permission mappings
 * Some routes use a generic moduleKey (like 'users_roles') but company/agency admins
 * have role-specific keys (like 'company_users_roles' or 'agency_users_roles')
 */
const MODULE_KEY_ALIASES: Record<string, string[]> = {
  users_roles: ['company_users_roles', 'agency_users_roles'],
  positions: ['company_positions'],
  // manage_agencies can be accessed by users with manage_profile permission
  // (for managing their own agency profile via AgencyProfileRedirect)
  manage_agencies: ['manage_profile'],
}

/**
 * Helper to find a key in submodules (reduces nesting depth)
 */
function findKeyInSubmodules(
  modulePermissions: ModulePermission[],
  targetKey: string,
): boolean | undefined {
  for (const mod of modulePermissions) {
    if (mod.submodules && mod.submodules.length > 0) {
      const found = mod.submodules.find((s) => s.key === targetKey)
      if (found) {
        return found.canView
      }
    }
  }
  return undefined
}

/**
 * Checks if user has permission for a moduleKey
 * Looks up the moduleKey in the user's modulePermissions and checks canView
 * Also searches submodules across all modules when submoduleKey is provided
 * Supports module key aliases for role-specific permission mappings
 */
const hasModulePermission = (
  modulePermissions: ModulePermission[] | undefined,
  moduleKey: string,
  submoduleKey?: string,
): boolean => {
  // Handle null, undefined, or empty array - allow access (API will validate)
  if (!modulePermissions || !Array.isArray(modulePermissions) || modulePermissions.length === 0) {
    // If no permissions data, allow access (will be restricted by API)
    return true
  }

  // First, try to find the module directly
  // Use case-insensitive comparison as a fallback for key mismatches
  const module =
    modulePermissions.find((m) => m.key === moduleKey) ||
    modulePermissions.find((m) => m.key?.toLowerCase() === moduleKey?.toLowerCase())

  if (module) {
    // If checking a submodule under this module
    if (submoduleKey && module.submodules && module.submodules.length > 0) {
      const submodule = module.submodules.find((s) => s.key === submoduleKey)
      if (submodule) {
        return submodule.canView
      }
      // Submodule not found under this module, fall back to parent module permission
    }
    return module.canView
  }

  // Module not found directly - if we have a submoduleKey, search across all modules' submodules
  // This handles cases like general_admin where 'roles_permissions' is under 'global' module
  if (submoduleKey) {
    for (const mod of modulePermissions) {
      if (mod.submodules && mod.submodules.length > 0) {
        const submodule = mod.submodules.find((s) => s.key === submoduleKey)
        if (submodule) {
          return submodule.canView
        }
      }
    }
  }

  // Also check if moduleKey itself exists as a submodule anywhere
  // This handles cases where a route's moduleKey is actually a submodule
  for (const mod of modulePermissions) {
    if (mod.submodules && mod.submodules.length > 0) {
      const submodule = mod.submodules.find((s) => s.key === moduleKey)
      if (submodule) {
        return submodule.canView
      }
    }
  }

  // Check for module key aliases (role-specific permission mappings)
  // This handles cases like 'users_roles' -> 'company_users_roles' for company admins
  const aliases = MODULE_KEY_ALIASES[moduleKey]
  if (aliases && aliases.length > 0) {
    for (const aliasKey of aliases) {
      // Check if alias exists as a direct module
      const aliasModule = modulePermissions.find((m) => m.key === aliasKey)
      if (aliasModule) {
        return aliasModule.canView
      }

      // Check if alias exists as a submodule (uses helper to reduce nesting)
      const submoduleResult = findKeyInSubmodules(modulePermissions, aliasKey)
      if (submoduleResult !== undefined) {
        return submoduleResult
      }
    }
  }

  // Module/submodule not in permissions list - user doesn't have access
  return false
}

// ============================================================================
// ROUTE RENDERER
// ============================================================================

interface RouteRendererProps {
  route: RouteConfig
  userRole: TCognitoRole | null
}

/**
 * Renders a single route with moduleKey permission checking
 * Uses modulePermissions from backendRolePermissions to check canView
 */
const RouteRenderer: React.FC<RouteRendererProps> = ({route, userRole}) => {
  const backendPermissions = useAppSelector((state) => state.root.backendRolePermissions)

  // Handle Navigate (redirect) routes
  if (route.component === 'Navigate' && route.meta?.redirectTo) {
    return <Navigate to={route.meta.redirectTo} replace />
  }

  // Check lazy components first, then regular components
  const LazyComponent = LAZY_COMPONENT_MAP[route.component]
  const RegularComponent = REGULAR_COMPONENT_MAP[route.component]

  if (!LazyComponent && !RegularComponent) {
    console.warn(`Component "${route.component}" not found in COMPONENT_MAP`)
    return null
  }

  // If route has moduleKey requirement, check modulePermissions
  // Note: Client-side permission checks are for UX only - API validates all requests server-side
  if (route.moduleKey) {
    // Platform admins have full access
    if (backendPermissions?.isPlatform) {
      // Platform admin bypass - allow access
    } else if (backendPermissions) {
      // Use Redux state (persisted by redux-persist, rehydrated on refresh)
      const hasPermission = hasModulePermission(
        backendPermissions.modulePermissions,
        route.moduleKey,
        route.submoduleKey,
      )

      if (!hasPermission) {
        return <Navigate to="/unauthorized" replace />
      }
    }
    // If backendPermissions is null (still loading), allow access
    // The component itself should handle loading state, and API will reject unauthorized requests
  }

  // Render the component
  if (LazyComponent) {
    return (
      <ChunkErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <LazyComponent />
        </Suspense>
      </ChunkErrorBoundary>
    )
  }

  if (RegularComponent) {
    return <RegularComponent />
  }

  return null
}

// ============================================================================
// LAYOUT ROUTES COMPONENT
// ============================================================================

interface LayoutRoutesProps {
  config: LayoutConfig
}

/**
 * Renders all routes for a specific layout
 */
const LayoutRoutes: React.FC<LayoutRoutesProps> = ({config}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {currentUser} = useAppSelector((state) => state.root)
  const [getUserByEmail] = useLazyGetUserByEmailQuery()
  const {showError} = useGlobalSnackbar()

  // EULA/Terms acceptance check
  const [eulaModalVisible, setEulaModalVisible] = useState(false)

  // Check if user has agreed to terms on component mount
  useEffect(() => {
    if (
      (currentUser && !currentUser.agreedToTermsDate) ||
      currentUser?.agreedToTermsDate === null
    ) {
      setEulaModalVisible(true)
    }
  }, [currentUser])

  // Handle EULA acceptance
  const handleEulaAccept = async () => {
    try {
      await userApi.updateAgreedToTermsDate(new Date().toISOString())

      // Refetch user to update Redux state with new agreedToTermsDate
      if (currentUser?.emailAddress) {
        const updatedUser = await getUserByEmail(currentUser.emailAddress).unwrap()
        dispatch(updateCurrentUser(updatedUser))
      }

      setEulaModalVisible(false)
    } catch (error: unknown) {
      showError(error)
    }
  }

  // Get user role from currentUser (cast to TCognitoRole)
  const userRole = (currentUser?.roles?.[0] as TCognitoRole) || null

  return (
    <Layout variant={config.variant}>
      <S.Container>
        <Routes>
          {config.routes.map((route) => (
            <Route
              key={route.id}
              path={route.path}
              index={route.meta?.index}
              element={<RouteRenderer route={route} userRole={userRole} />}
            />
          ))}
        </Routes>
      </S.Container>

      {/* EULA Dialog - Shows when user hasn't agreed to terms */}
      <EulaDialog
        isModalOpen={eulaModalVisible}
        handleClick={async (accepted: boolean) => {
          if (accepted) {
            await handleEulaAccept()
          } else {
            setEulaModalVisible(false)
            navigate('/signin')
          }
        }}
        onModalClose={() => {
          setEulaModalVisible(false)
          navigate('/signin')
        }}
      />
    </Layout>
  )
}

// ============================================================================
// MAIN APP ROUTES COMPONENT
// ============================================================================

/**
 * Determines the layout variant from the role's category
 * This is the primary method for custom roles that don't follow naming patterns
 */
const getVariantFromCategory = (
  category: 'Platform' | 'MSP' | 'Agency' | 'Company' | undefined,
): LayoutVariant | null => {
  switch (category) {
    case 'Platform':
      return 'platform'
    case 'MSP':
      // MSP category uses 'agency' variant for navigation (has manage_profile, etc.)
      return 'agency'
    case 'Agency':
      return 'agency'
    case 'Company':
      return 'company'
    default:
      return null // No category available, fall back to role name matching
  }
}

/**
 * Determines the layout variant from the user's role name
 * Used as fallback when category is not available
 * URLs no longer contain role prefixes - variant is determined from user data
 */
const getVariantFromRoleName = (roleName: string | undefined): LayoutVariant => {
  if (!roleName) return 'platform' // Default fallback

  const lower = roleName.toLowerCase()

  // Platform admin roles (full platform access)
  if (lower === 'platformadmin') {
    return 'platform'
  }

  // General admin (cross-agency admin with limited modules)
  if (lower === 'general_admin') {
    return 'general-admin'
  }

  // Main agency admin (MSP parent agency)
  if (lower === 'mainagencyadmin') {
    return 'main-agency'
  }

  // MSP specific agency admin
  if (lower === 'mspacencyadmin') {
    return 'msp'
  }

  // Regular agency admin and other agency roles
  // Note: agencyadmin can be for both MSP and non-MSP agencies
  // Using 'agency' variant which has manage_profile route
  if (lower === 'agencyadmin' || lower.includes('agency')) {
    return 'agency'
  }

  // Company roles
  if (lower === 'companyadmin' || lower === 'companyuser') {
    return 'company'
  }

  // Default to platform
  return 'platform'
}

/**
 * Gets layout config by variant
 */
const getLayoutConfigByVariant = (variant: LayoutVariant): LayoutConfig | undefined => {
  switch (variant) {
    case 'platform':
      return PLATFORM_ADMIN_CONFIG
    case 'general-admin':
      // General admin uses platform routes but has its own nav menu (generalAdminList)
      return {...PLATFORM_ADMIN_CONFIG, variant: 'general-admin'}
    case 'main-agency':
      return MAIN_AGENCY_ADMIN_CONFIG
    case 'msp':
      return MSP_AGENCY_ADMIN_CONFIG
    case 'agency':
      return AGENCY_ADMIN_CONFIG
    case 'company':
      return COMPANY_ADMIN_CONFIG
    default:
      return undefined
  }
}

/**
 * Main unified routes component
 *
 * Variant is now determined from the user's role stored in Redux,
 * NOT from URL prefixes. URLs are role-agnostic.
 *
 * Examples:
 * - /manage-companies (same URL for all roles)
 * - /users-roles (same URL for all roles)
 * - What the user sees is determined by their permissions, not the URL
 */
export const AppRoutes: React.FC = () => {
  const {currentUser, backendRolePermissions} = useAppSelector((state) => state.root)

  // Get the user's primary role (first role in array)
  const userRole = currentUser?.roles?.[0]

  // Determine variant: first try category from backend, then fall back to role name pattern
  // This ensures custom roles (like "testing_custom_role") use the correct layout
  // based on their category (Agency, Company, etc.) rather than role name patterns
  const categoryVariant = getVariantFromCategory(backendRolePermissions?.category)
  const variant = categoryVariant ?? getVariantFromRoleName(userRole)

  const config = getLayoutConfigByVariant(variant)

  if (!config) {
    console.error(`No layout config found for variant: ${variant}`)
    return <Navigate to="/signin" replace />
  }

  return <LayoutRoutes config={config} />
}

export default AppRoutes
